import { useCallback, useEffect, useState } from 'react';

import { deviceCalendarService } from '@/lib/calendar/DeviceCalendarService';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { Toast } from '@/components/ui/molecules/toast';
import type { LocalFamilyMembersStore } from '@/lib/local/LocalFamilyMembersStore';
import type {
  CalendarEvent,
  CreateCalendarEventInput,
  UpdateCalendarEventInput,
} from '@/types/events';
import type { FamilyMember } from '@/types/family-member';

type UseHomeEventModalArgs = {
  refreshEvents: () => Promise<void>;
  familyMembersStore: LocalFamilyMembersStore | null;
};

/**
 * Owns the Home screen's view/edit event modal: selection state, the device
 * calendars and family members the modal needs, and save/delete handlers with
 * user-facing error toasts.
 */
export function useHomeEventModal({ refreshEvents, familyMembersStore }: UseHomeEventModalArgs) {
  const [showViewEventModal, setShowViewEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isSubmittingEvent, setIsSubmittingEvent] = useState(false);
  const [writableCalendars, setWritableCalendars] = useState<DeviceCalendar[]>([]);
  const [allCalendars, setAllCalendars] = useState<DeviceCalendar[]>([]);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);

  useEffect(() => {
    const loadCalendars = async () => {
      try {
        const calendars = await deviceCalendarService.getDeviceCalendars();
        setAllCalendars(calendars);
        setWritableCalendars(calendars.filter((cal: DeviceCalendar) => cal.allowsModifications));
      } catch (err) {
        console.error('[useHomeEventModal] Failed to load calendars:', err);
      }
    };
    loadCalendars();
  }, []);

  useEffect(() => {
    if (!familyMembersStore) return;
    const loadFamilyMembers = async () => {
      try {
        setFamilyMembers(await familyMembersStore.getAll());
      } catch (err) {
        console.error('[useHomeEventModal] Failed to load family members:', err);
      }
    };
    loadFamilyMembers();
  }, [familyMembersStore]);

  const handleUpcomingEventPress = useCallback((event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowViewEventModal(true);
  }, []);

  const handleViewEventModalDismiss = useCallback(() => {
    setShowViewEventModal(false);
    setSelectedEvent(null);
  }, []);

  const handleViewEventModalSave = useCallback(
    async (
      input: (CreateCalendarEventInput | UpdateCalendarEventInput) & { targetCalendarId?: string },
    ) => {
      if (!selectedEvent) return;
      setIsSubmittingEvent(true);
      try {
        await deviceCalendarService.updateEvent(selectedEvent.id, input as UpdateCalendarEventInput);
        await refreshEvents();
        setShowViewEventModal(false);
        setSelectedEvent(null);
      } catch (err) {
        console.error('[useHomeEventModal] Failed to update event:', err);
        Toast.show("Couldn't save changes to this event. Please try again.", { type: 'error' });
      } finally {
        setIsSubmittingEvent(false);
      }
    },
    [selectedEvent, refreshEvents],
  );

  const handleViewEventModalDelete = useCallback(async () => {
    if (!selectedEvent) return;
    setIsSubmittingEvent(true);
    try {
      await deviceCalendarService.deleteEvent(selectedEvent.id);
      await refreshEvents();
      setShowViewEventModal(false);
      setSelectedEvent(null);
    } catch (err) {
      console.error('[useHomeEventModal] Failed to delete event:', err);
      Toast.show("Couldn't delete this event. Please try again.", { type: 'error' });
    } finally {
      setIsSubmittingEvent(false);
    }
  }, [selectedEvent, refreshEvents]);

  return {
    showViewEventModal,
    selectedEvent,
    isSubmittingEvent,
    writableCalendars,
    allCalendars,
    familyMembers,
    handleUpcomingEventPress,
    handleViewEventModalDismiss,
    handleViewEventModalSave,
    handleViewEventModalDelete,
  };
}
